import { Box } from '@spark-web/box';
import { Container } from '@spark-web/container';
import { Inline } from '@spark-web/inline';
import { Text } from '@spark-web/text';
import { TextLink } from '@spark-web/text-link';
import { ReactElement } from 'react';

import { BRIGHTE_PHONE_NUMBER } from '~/constants';
import { MarketplacePartner } from '~/types/marketplace';
import { getBrighteSitePartnerUrl } from '~/utils/formatter';

import { ExclamationIcon, InfoIcon } from './icon';

export type AlertMessageTone = 'info' | 'critical';

type AlertMessageProps = {
  tone: AlertMessageTone;
  children: ReactElement;
  partner?: MarketplacePartner;
};

const IconByTone = {
  critical: ExclamationIcon,
  info: InfoIcon,
};

const ALERT_ICON_SIZE = 16;

const AlertMessage = ({ tone, children, partner }: AlertMessageProps) => {
  const AlertIcon = IconByTone[tone];
  return (
    <Box background={tone} flex={1}>
      <Box
        display="flex"
        flexDirection="row"
        gap="small"
        marginLeft="xsmall"
        background={`${tone}Low`}
        padding="xlarge"
        paddingX="medium"
      >
        <AlertIcon brighteTone={tone} partnerSize={ALERT_ICON_SIZE} partner={partner} brighteSize="small" />
        {children}
      </Box>
    </Box>
  );
};

type ErrorDetails = {
  tone?: AlertMessageTone;
  description?: ReactElement;
  icon?: string;
};

export type ErrorType =
  | 'INVALID_PARTNER'
  | 'HOUSEHOLD_DETAILS_REQUIRED'
  | 'REQUEST_ERROR'
  | 'GENERIC_ERROR'
  | 'GENERIC_PARAMS_REQUIRED';

type AlertProps = {
  partner?: MarketplacePartner;
  error?: ErrorType;
};

export const Alert = ({ partner, error }: AlertProps) => {
  const ERROR_DETAILS_MAP: Record<ErrorType, ErrorDetails> = {
    INVALID_PARTNER: {
      tone: 'critical',
      description: (
        <Inline gap={{ mobile: 'large', desktop: 'small', tablet: 'small' }} alignSelf="center">
          <Text size="small">
            {`We're sorry, we are unable to retrieve your search results. Please go back to the previous screen and
            re-enter your details.`}
          </Text>
          <Text tone="info" size="small">
            <TextLink href="" onClick={() => history.back()} weight="regular">
              Go back.
            </TextLink>
          </Text>
        </Inline>
      ),
    },
    HOUSEHOLD_DETAILS_REQUIRED: {
      tone: 'info',
      description: (
        <Inline gap={{ mobile: 'large', desktop: 'small', tablet: 'small' }} alignSelf="center">
          <Text size="small">You need to enter your household details in order to view search results.</Text>
          <Text tone="info" size="small">
            <TextLink href={getBrighteSitePartnerUrl(partner)}>Enter household details.</TextLink>
          </Text>
        </Inline>
      ),
    },
    REQUEST_ERROR: {
      tone: 'critical',
      description: (
        <Text size="small" role="alert">
          {`We're sorry, we are unable to submit your request. Please try again later or call the Brighte team on `}
          <TextLink href={`tel:${BRIGHTE_PHONE_NUMBER}`}>{BRIGHTE_PHONE_NUMBER}</TextLink>
        </Text>
      ),
    },
    GENERIC_ERROR: {
      tone: 'critical',
      description: (
        <Inline gap={{ mobile: 'large', desktop: 'small', tablet: 'small' }} alignSelf="center">
          <Text size="small" role="alert">
            {`We're sorry, we are unable to process your request. Please try again later.`}
          </Text>
        </Inline>
      ),
    },
    GENERIC_PARAMS_REQUIRED: {
      tone: 'info',
      description: (
        <Inline gap={{ mobile: 'large', desktop: 'xsmall', tablet: 'small' }} alignSelf="center">
          <Text size="small">To get started, head to</Text>
          <Text tone="info" size="small">
            <TextLink href={getBrighteSitePartnerUrl(partner)}>
              {getBrighteSitePartnerUrl(partner).replace('https://', '')}
            </TextLink>
          </Text>
        </Inline>
      ),
    },
  };

  const alertProps = error && ERROR_DETAILS_MAP[error];
  return alertProps?.tone ? (
    <Container size="large">
      <Box padding="large">
        {alertProps.description ? (
          <AlertMessage tone={alertProps.tone} partner={partner}>
            {alertProps?.description}
          </AlertMessage>
        ) : null}
      </Box>
    </Container>
  ) : null;
};
